import { APP_BASE_HREF } from '@angular/common';
import { Optional, Inject, Injectable } from '@angular/core';
import { ApiBrokerRequestHandler, ApiRequest, AppInsightsService, AppSession,
  AppSessionService, WINDOW_LOCATION } from '@eucoms/common-components';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ApiLoggingService {

  constructor(protected appInsights: AppInsightsService,
    protected appSessionService: AppSessionService,
    @Optional() @Inject(APP_BASE_HREF) protected appBaseHref: string,
    @Optional() @Inject(WINDOW_LOCATION) protected location: any,
  ) {
    ApiLoggingProxyHandler.initServices(appInsights, appSessionService, 'eu-web-outages');
  }

  public handleRequest<TResponse = any, TRequest = any>(request: ApiRequest<TRequest>,
    next: ApiBrokerRequestHandler<TResponse, TRequest>): Observable<TResponse> {

    const locationPath = this.location?.pathname?.toLowerCase() ?? 'unknown';

    return next(request).pipe(
        map((response) => {
          try {
            const proxyDiscriminator = this.tempTypeNameMatcher(request.url, request.method);
            return new Proxy(
              response as any,
              new ApiLoggingProxyHandler([`{${proxyDiscriminator}}`], locationPath)
            ) as TResponse;
        } catch (e) {
          console.error('Error creating proxy', e);
          return response;
        }
      }));
  }

  private tempTypeNameMatcher(url: string, method: string): string {
    if (method?.toLowerCase() !== 'get') {
      return `${method?.toUpperCase()} ${url}`;
    }

    // regex below explanation:
    // /\/auth\/accounts\/                <- matches the string '/auth/accounts/' exactly
    // (\d{10,11}|{{account[_]?number}})  <- matches a 10-11 digit number or the string '{{account_number}}' or '{{accountnumber}}
    // (\?.*)?$                           <- matches an optional query string
    // /i                                 <- case insensitive
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})(\?.*)?$/i)) { return 'AccountDetails'; }
    if (url.match(/\/auth\/accounts(\?.*)?$/i)) { return 'AccountList'; }
    // /auth/accounts/1776902000/outage/plannedOutage
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})\/outage\/plannedOutage(\?.*)?$/i)) { return 'PlannedOutages'; }
    // /auth/accounts/1776902000/outage
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})\/outage(\?.*)?$/i)) { return 'OutageStatus'; }
    // /auth/wallet/query
    if (url.match(/\/auth\/wallet\/query(\?.*)?$/i)) { return 'CustomerWallet'; }
    // /auth/accounts/1776902000/outage/tracker
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})\/outage\/tracker(\?.*)?$/i)) { return 'OutageTracker'; }
    // /pref/accounts/{{account[_?]number}}/preferences
    if (url.match(/\/pref\/accounts\/(\d{10,11}|{{account[_?]number}})\/preferences(\?.*)?$/i)) { return 'CustomerPreferences'; }
    // /auth/accounts/1776902000/payments
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})\/payments(\?.*)?$/i)) { return 'AccountPayments'; }
    // /auth/accounts/1776902000/mfa/enabled
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})\/mfa\/enabled(\?.*)?$/i)) { return 'CustomerMFAEnabled'; }
    // /auth/mma/prefs/1776902000
    if (url.match(/\/auth\/mma\/prefs\/(\d{10,11}|{{account[_?]number}})(\?.*)?$/i)) { return 'MMAPreferences'; }
    // /auth/accounts/1776902000/billing/duedate
    if (url.match(/\/auth\/accounts\/(\d{10,11}|{{account[_?]number}})\/billing\/duedate(\?.*)?$/i)) { return 'BillingDueDate'; }

    return `GET ${url}`;
  }
}

export class ApiLoggingProxyHandler<T extends object> implements ProxyHandler<T> {
  constructor(
    private path: Array<string|number|Symbol> = [],
    private locationPath: string = 'unknown'
  ) {
    ApiLoggingProxyHandler.init(this.locationPath);
  }
  static accessedProperties: Map<string, Array<string>> = null;
  static isProxy = Symbol('isProxy');
  static isInitialized: Map<string, boolean> = null;
  static appInsights: AppInsightsService = null;
  static appSessionService: AppSessionService = null;
  static modifiedProperties: Map<string, Array<string>> = null;
  static appName: string;

  static locationSubjects: Map<string, Subject<string>> = new Map<string, Subject<string>>();

  static initServices(appInsights: AppInsightsService, appSessionService: AppSessionService, appName: string = '') {
      ApiLoggingProxyHandler.appInsights = appInsights;
      ApiLoggingProxyHandler.appSessionService = appSessionService;
      ApiLoggingProxyHandler.appName = appName;
  }

  static writeToAppInsights(locationPath: string) {
    let locationSubject = this.locationSubjects.get(locationPath);
    const appSession: AppSession = ApiLoggingProxyHandler.appSessionService?.isUserAuthenticated()
      ? ApiLoggingProxyHandler.appSessionService?.session
      : null;

    if (!locationSubject) {
      locationSubject = new Subject<string>();
      locationSubject.asObservable().pipe(
        debounceTime(100) // debounce a bit so that we don't log too many events
      ).subscribe({
          next: (value) => {
            this.appInsights.logEvent('API Broker Logging Proxy', {
              location: locationPath,
              accountType: ApiLoggingProxyHandler.appSessionService?.isUserAuthenticated()
                    ? (ApiLoggingProxyHandler.appSessionService?.isResidential() ? 'auth-residential' : 'auth-non-residential')
                    : 'anon',
              customerNumber: appSession?.customerNumber,
              accountNumber: appSession?.accountNumber,
              accessedProperties: ApiLoggingProxyHandler.accessedProperties.get(locationPath),
              appName: ApiLoggingProxyHandler.appName
            });
          }
      });
      this.locationSubjects.set(locationPath, locationSubject);
    }
    locationSubject.next(locationPath);
  }

  static init(location: string) {
    if (!ApiLoggingProxyHandler.isInitialized) {
      ApiLoggingProxyHandler.accessedProperties = new Map<string, Array<string>>();
      ApiLoggingProxyHandler.isInitialized = new Map<string, boolean>();
      ApiLoggingProxyHandler.modifiedProperties = new Map<string, Array<string>>();
    }

    if (!ApiLoggingProxyHandler.isInitialized.has(location)
      || !ApiLoggingProxyHandler.isInitialized.get(location)) {
      ApiLoggingProxyHandler.isInitialized.set(location, true);
      ApiLoggingProxyHandler.accessedProperties.set(location, new Array<string>());
      ApiLoggingProxyHandler.modifiedProperties.set(location, new Array<string>());
    }
  }

  static getFullPropName(path: Array<string|number|Symbol>) {
    const mapKey = function(key: string|number|Symbol) {
      return typeof key === 'number' ? `[]` : key.toString();
    };
    return path.map(mapKey).join('.');
  }

  static getOrWrap(target: any,
    value: any,
    path: Array<string|number|Symbol>,
    key: string|number|Symbol,
    locationPath: string): any {
      if (key === ApiLoggingProxyHandler.isProxy) { return true; }

      if (typeof key === 'symbol') { return target[key]; }
      if (typeof value === 'undefined') { return; }
      if (typeof value === 'function') { return value.bind(target); }

      const fullPropName = ApiLoggingProxyHandler.getFullPropName([...path, key]);

      ApiLoggingProxyHandler.logApiResponseAccess(value, fullPropName, locationPath);

      if (typeof value === 'object'
        && value != null
        && !(value instanceof Date)
        && !(value instanceof Array)
        && !value?.[ApiLoggingProxyHandler.isProxy]) {
        return new Proxy(
            value,
            new ApiLoggingProxyHandler([...path, key], locationPath)
        );
      }

      if (value instanceof Array) {
        const proxyArray = value.map((item, index) => {
          return ApiLoggingProxyHandler.getOrWrap(
            value,
            item,
            [...path, key],
            index,
            locationPath);
        });
        proxyArray[ApiLoggingProxyHandler.isProxy] = true;
        return proxyArray;
      }

      if (value instanceof Date) {
        value[ApiLoggingProxyHandler.isProxy] = true;
        return value;
      }
      return value;
  }

  static getPropertiesLoggingKey(locationPath: string) {
    return `_midMod:ApiBrokerLoggingProxy:${locationPath}`;
  }

  static logApiResponseAccess(target: any,
    fullPropertyName: any,
    locationPath: string) {

    const alreadyModified = (ApiLoggingProxyHandler.modifiedProperties.get(locationPath))
      .filter(key => fullPropertyName.startsWith(key))
      ?.length > 0;

    const propName = alreadyModified
        ? '[modified]' + fullPropertyName
        : fullPropertyName;

    // retrieve the number of existing entries from accessedProperties where the key starts with fullPropName
    const alreadyIncluded = ApiLoggingProxyHandler.accessedProperties.get(locationPath)
        .filter(key => key.startsWith(propName))
        ?.length > 0;
    if (!alreadyIncluded) {

      // remove all entries from accessedProperties where the propName starts with its key
      // (this is to log only the "leaf" properties, not the parent objects)
      const newLeafProperties = ApiLoggingProxyHandler.accessedProperties.get(locationPath)
        .filter(key => !propName.startsWith(key));
      newLeafProperties.push(propName);
      ApiLoggingProxyHandler.accessedProperties.set(locationPath, newLeafProperties);

      localStorage.setItem(ApiLoggingProxyHandler.getPropertiesLoggingKey(locationPath),
        'API response accessed properties: \n' + (ApiLoggingProxyHandler.accessedProperties.get(locationPath)).join('\n'));
      ApiLoggingProxyHandler.writeToAppInsights(locationPath);
    }
  }

  get(target: T, key: string|number|Symbol): any {
    return ApiLoggingProxyHandler.getOrWrap(target,
      (target as any)?.[key as any],
      this.path,
      key,
      this.locationPath);
  }

  set(target: T, key: string|number|Symbol, value: any): boolean {
    if ((target as any)[key as any] !== value) {
      console.warn('Setting property after proxy has been instantiated!:', key, value);
      const fullPropName = ApiLoggingProxyHandler.getFullPropName([...this.path, key]);

      // don't need any child properties of fullPropName because we're setting the value of a parent node
      const newModifiedProperties = (ApiLoggingProxyHandler.modifiedProperties.get(this.locationPath))
        .filter(propName => !propName.startsWith(fullPropName));
      newModifiedProperties.push(fullPropName);
      ApiLoggingProxyHandler.modifiedProperties.set(this.locationPath, newModifiedProperties);
    }
    (target as any)[key as any] = value;
    return true;
  }
}
